import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy, doc, getDoc, updateDoc, deleteField, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import type { Organization, Recipe } from '../../types/index';
import { ChefHat, Calendar, Globe, Lock, Search, CheckSquare, Square, Loader, X, Tags as TagsIcon } from 'lucide-react';
import { generateRecipeSeoUrl } from '../../utils/seoUtils';
import { TagEditor } from './TagEditor';
import { useAuthReady } from '../../hooks/useAuthReady';

interface TagWithRecipes {
  id: string;
  name: string;
  color: string;
  recipeIds: string[];
}

interface Cookbook {
  organizationId: string;
  tags: { [tagId: string]: TagWithRecipes };
}

interface Tag {
  id: string;
  name: string;
  color: string;
}

// Map recipeId -> tag names
type RecipeTagsMap = { [recipeId: string]: string[] };

export const RecipeManagement: React.FC = () => {
  const authReady = useAuthReady(true);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [filteredRecipes, setFilteredRecipes] = useState<Recipe[]>([]);
  const [recipeTags, setRecipeTags] = useState<RecipeTagsMap>({});
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(true);
  const [isLoadingRecipes, setIsLoadingRecipes] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPublic, setFilterPublic] = useState<'all' | 'public' | 'private'>('all');
  const [error, setError] = useState<string | null>(null);
  
  // Bulk edit states
  const [selectedRecipeIds, setSelectedRecipeIds] = useState<Set<string>>(new Set());
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [bulkUpdateProgress, setBulkUpdateProgress] = useState({ current: 0, total: 0 });
  const [showBulkTagModal, setShowBulkTagModal] = useState(false);
  const [bulkTagAction, setBulkTagAction] = useState<'add' | 'remove'>('add');

  // Charger les organisations
  useEffect(() => {
    // Wait for auth to be ready before fetching
    if (!authReady) {
      console.log('⏳ Waiting for auth to be ready before fetching organizations...');
      return;
    }

    const fetchOrganizations = async () => {
      try {
        console.log('📋 Fetching organizations for RecipeManagement...');
        const orgsQuery = query(collection(db, 'organizations'));
        const snapshot = await getDocs(orgsQuery);
        const orgs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Organization));
        setOrganizations(orgs);
        console.log(`✅ Loaded ${orgs.length} organizations`);
      } catch (error) {
        console.error('❌ Error fetching organizations:', error);
        setError('Erreur lors du chargement des organisations');
      } finally {
        setIsLoadingOrgs(false);
      }
    };

    fetchOrganizations();
  }, [authReady]);

  // Charger les recettes quand une organisation est sélectionnée
  useEffect(() => {
    if (!selectedOrgId) {
      setRecipes([]);
      setFilteredRecipes([]);
      setRecipeTags({});
      return;
    }

    const fetchRecipesAndTags = async () => {
      setIsLoadingRecipes(true);
      setError(null);
      try {
        // 1. Charger les recettes
        const recipesQuery = query(
          collection(db, 'recipes'),
          where('organizationId', '==', selectedOrgId),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(recipesQuery);
        const recipesData = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
            updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt),
          } as Recipe;
        });
        setRecipes(recipesData);
        setFilteredRecipes(recipesData);

        // 2. Charger le cookbook pour récupérer les tags
        const cookbookRef = doc(db, 'cookbooks', selectedOrgId);
        const cookbookSnap = await getDoc(cookbookRef);
        
        if (cookbookSnap.exists()) {
          const cookbook = cookbookSnap.data() as Cookbook;
          
          // 3. Créer un map recipeId -> tag names
          const tagsMap: RecipeTagsMap = {};
          const tags: Tag[] = [];
          
          if (cookbook.tags) {
            Object.values(cookbook.tags).forEach((tag: TagWithRecipes) => {
              // Build tags list for selector
              tags.push({
                id: tag.id,
                name: tag.name,
                color: tag.color
              });
              
              // Build recipe -> tags map
              tag.recipeIds?.forEach((recipeId: string) => {
                if (!tagsMap[recipeId]) {
                  tagsMap[recipeId] = [];
                }
                tagsMap[recipeId].push(tag.name);
              });
            });
          }
          
          setRecipeTags(tagsMap);
          setAvailableTags(tags);
          console.log('Tags loaded:', tagsMap);
          console.log('Available tags:', tags);
        } else {
          console.log('No cookbook found for organization:', selectedOrgId);
          setRecipeTags({});
          setAvailableTags([]);
        }
      } catch (error) {
        console.error('Error fetching recipes and tags:', error);
        setError('Erreur lors du chargement des recettes');
      } finally {
        setIsLoadingRecipes(false);
      }
    };

    fetchRecipesAndTags();
  }, [selectedOrgId]);

  // Filtrer les recettes
  useEffect(() => {
    let filtered = [...recipes];

    // Filtre par recherche
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(recipe => {
        const nameMatch = recipe.name.toLowerCase().includes(term);
        const tagsMatch = recipeTags[recipe.id]?.some((tag: string) => 
          tag.toLowerCase().includes(term)
        );
        return nameMatch || tagsMatch;
      });
    }

    // Filtre public/privé
    if (filterPublic !== 'all') {
      filtered = filtered.filter(recipe =>
        filterPublic === 'public' ? recipe.isPublic : !recipe.isPublic
      );
    }

    setFilteredRecipes(filtered);
    
    // Clear selections when filters change
    setSelectedRecipeIds(new Set());
  }, [searchTerm, filterPublic, recipes, recipeTags]);

  // Bulk selection handlers
  const handleToggleRecipe = (recipeId: string) => {
    const newSelection = new Set(selectedRecipeIds);
    if (newSelection.has(recipeId)) {
      newSelection.delete(recipeId);
    } else {
      newSelection.add(recipeId);
    }
    setSelectedRecipeIds(newSelection);
  };

  const handleSelectAll = () => {
    const allIds = new Set(filteredRecipes.map(r => r.id));
    setSelectedRecipeIds(allIds);
  };

  const handleDeselectAll = () => {
    setSelectedRecipeIds(new Set());
  };

  // Tag management handlers
  const handleAddTag = async (recipeId: string, tagId: string) => {
    if (!selectedOrgId) return;

    try {
      const cookbookRef = doc(db, 'cookbooks', selectedOrgId);
      await updateDoc(cookbookRef, {
        [`tags.${tagId}.recipeIds`]: arrayUnion(recipeId),
        [`tags.${tagId}.updatedAt`]: new Date().toISOString()
      });

      // Refresh tags
      const tag = availableTags.find(t => t.id === tagId);
      if (tag) {
        setRecipeTags(prev => ({
          ...prev,
          [recipeId]: [...(prev[recipeId] || []), tag.name]
        }));
      }
    } catch (error) {
      console.error('Error adding tag:', error);
      throw error;
    }
  };

  const handleRemoveTag = async (recipeId: string, tagId: string) => {
    if (!selectedOrgId) return;

    try {
      const cookbookRef = doc(db, 'cookbooks', selectedOrgId);
      await updateDoc(cookbookRef, {
        [`tags.${tagId}.recipeIds`]: arrayRemove(recipeId),
        [`tags.${tagId}.updatedAt`]: new Date().toISOString()
      });

      // Refresh tags
      const tag = availableTags.find(t => t.id === tagId);
      if (tag) {
        setRecipeTags(prev => ({
          ...prev,
          [recipeId]: (prev[recipeId] || []).filter(name => name !== tag.name)
        }));
      }
    } catch (error) {
      console.error('Error removing tag:', error);
      throw error;
    }
  };

  // Bulk tag change handler
  const handleBulkTagChange = async (tagId: string, action: 'add' | 'remove') => {
    if (selectedRecipeIds.size === 0 || !selectedOrgId) return;

    const tag = availableTags.find(t => t.id === tagId);
    if (!tag) return;

    const confirmMessage = action === 'add'
      ? `Ajouter le tag "${tag.name}" à ${selectedRecipeIds.size} recette(s) ?`
      : `Retirer le tag "${tag.name}" de ${selectedRecipeIds.size} recette(s) ?`;

    if (!window.confirm(confirmMessage)) return;

    setIsBulkUpdating(true);
    setBulkUpdateProgress({ current: 0, total: selectedRecipeIds.size });
    
    let successCount = 0;
    let errorCount = 0;

    try {
      const cookbookRef = doc(db, 'cookbooks', selectedOrgId);
      const selectedRecipesArray = Array.from(selectedRecipeIds);
      
      for (let i = 0; i < selectedRecipesArray.length; i++) {
        const recipeId = selectedRecipesArray[i];
        setBulkUpdateProgress({ current: i + 1, total: selectedRecipesArray.length });

        try {
          if (action === 'add') {
            await updateDoc(cookbookRef, {
              [`tags.${tagId}.recipeIds`]: arrayUnion(recipeId),
              [`tags.${tagId}.updatedAt`]: new Date().toISOString()
            });
          } else {
            await updateDoc(cookbookRef, {
              [`tags.${tagId}.recipeIds`]: arrayRemove(recipeId),
              [`tags.${tagId}.updatedAt`]: new Date().toISOString()
            });
          }
          
          successCount++;
        } catch (error) {
          console.error(`Error updating tag for recipe ${recipeId}:`, error);
          errorCount++;
        }
      }

      // Refresh cookbook data
      const cookbookSnap = await getDoc(cookbookRef);
      if (cookbookSnap.exists()) {
        const cookbook = cookbookSnap.data() as Cookbook;
        const tagsMap: RecipeTagsMap = {};
        
        if (cookbook.tags) {
          Object.values(cookbook.tags).forEach((tag: TagWithRecipes) => {
            tag.recipeIds?.forEach((recipeId: string) => {
              if (!tagsMap[recipeId]) {
                tagsMap[recipeId] = [];
              }
              tagsMap[recipeId].push(tag.name);
            });
          });
        }
        
        setRecipeTags(tagsMap);
      }

      // Show results
      let message = `✅ ${successCount} recette(s) mise(s) à jour`;
      if (errorCount > 0) {
        message += `\n❌ ${errorCount} erreur(s)`;
      }
      alert(message);

      setShowBulkTagModal(false);
      
    } catch (error) {
      console.error('Bulk tag update error:', error);
      alert('Erreur lors de la mise à jour en masse des tags.');
    } finally {
      setIsBulkUpdating(false);
      setBulkUpdateProgress({ current: 0, total: 0 });
    }
  };

  // Bulk visibility change handler
  const handleBulkVisibilityChange = async (makePublic: boolean) => {
    if (selectedRecipeIds.size === 0) return;

    const confirmMessage = makePublic
      ? `Rendre ${selectedRecipeIds.size} recette(s) publiques ? Un URL SEO sera généré pour chaque recette.`
      : `Rendre ${selectedRecipeIds.size} recette(s) privées ? Les URLs SEO seront supprimés.`;

    if (!window.confirm(confirmMessage)) return;

    setIsBulkUpdating(true);
    setBulkUpdateProgress({ current: 0, total: selectedRecipeIds.size });
    
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    try {
      const selectedRecipes = recipes.filter(r => selectedRecipeIds.has(r.id));
      
      for (let i = 0; i < selectedRecipes.length; i++) {
        const recipe = selectedRecipes[i];
        setBulkUpdateProgress({ current: i + 1, total: selectedRecipes.length });

        try {
          const recipeRef = doc(db, 'recipes', recipe.id);
          
          if (makePublic) {
            // Generate and add seoUrl when making public
            const seoUrl = generateRecipeSeoUrl(recipe.name, recipe.id);
            await updateDoc(recipeRef, {
              isPublic: true,
              seoUrl: seoUrl,
              updatedAt: new Date().toISOString()
            });
          } else {
            // Remove seoUrl when making private
            await updateDoc(recipeRef, {
              isPublic: false,
              seoUrl: deleteField(),
              updatedAt: new Date().toISOString()
            });
          }
          
          successCount++;
        } catch (error) {
          console.error(`Error updating recipe ${recipe.id}:`, error);
          errorCount++;
          errors.push(`${recipe.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      // Refresh recipes after bulk update
      if (successCount > 0) {
        const recipesQuery = query(
          collection(db, 'recipes'),
          where('organizationId', '==', selectedOrgId),
          orderBy('createdAt', 'desc')
        );
        const snapshot = await getDocs(recipesQuery);
        const recipesData = snapshot.docs.map(docSnap => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
            updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt),
          } as Recipe;
        });
        setRecipes(recipesData);
        setFilteredRecipes(recipesData);
      }

      // Show results
      let message = `✅ ${successCount} recette(s) mise(s) à jour avec succès`;
      if (errorCount > 0) {
        message += `\n❌ ${errorCount} erreur(s):\n${errors.slice(0, 3).join('\n')}`;
        if (errors.length > 3) {
          message += `\n... et ${errors.length - 3} autre(s) erreur(s)`;
        }
      }
      alert(message);

      // Clear selection
      setSelectedRecipeIds(new Set());
      
    } catch (error) {
      console.error('Bulk update error:', error);
      alert('Erreur lors de la mise à jour en masse. Veuillez réessayer.');
    } finally {
      setIsBulkUpdating(false);
      setBulkUpdateProgress({ current: 0, total: 0 });
    }
  };

  const selectedOrg = organizations.find(org => org.id === selectedOrgId);

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ChefHat className="h-7 w-7 text-menioo-green" />
              Gestion des Recettes
            </h1>
            <p className="text-gray-600 mt-1">
              Visualisez et gérez toutes les recettes par organisation
            </p>
          </div>
        </div>
      </div>

      {/* Organisation Selector */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Sélectionnez une organisation
        </label>
        {isLoadingOrgs ? (
          <div className="text-gray-500">Chargement des organisations...</div>
        ) : (
          <select
            value={selectedOrgId}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-menioo-green focus:border-menioo-green"
          >
            <option value="">-- Choisir une organisation --</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>
                {org.name} ({org.members?.length || 0} membres)
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Filters & Search */}
      {selectedOrgId && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher par titre ou tag..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-menioo-green focus:border-menioo-green"
                />
              </div>
            </div>

            {/* Filter Public/Private */}
            <div className="sm:w-48">
              <select
                value={filterPublic}
                onChange={(e) => setFilterPublic(e.target.value as 'all' | 'public' | 'private')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-menioo-green focus:border-menioo-green"
              >
                <option value="all">Toutes</option>
                <option value="public">Publiques</option>
                <option value="private">Privées</option>
              </select>
            </div>
          </div>

          {/* Stats */}
          {selectedOrg && (
            <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
              <span className="font-medium">
                {filteredRecipes.length} recette{filteredRecipes.length !== 1 ? 's' : ''}
              </span>
              {filteredRecipes.length !== recipes.length && (
                <span className="text-gray-400">
                  (sur {recipes.length} au total)
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Recipes Table */}
      {selectedOrgId && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {isLoadingRecipes ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-menioo-green"></div>
              <p className="mt-4 text-gray-600">Chargement des recettes...</p>
            </div>
          ) : filteredRecipes.length === 0 ? (
            <div className="text-center py-12">
              <ChefHat className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                {recipes.length === 0
                  ? 'Aucune recette pour cette organisation'
                  : 'Aucune recette ne correspond aux filtres'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left">
                      <button
                        onClick={selectedRecipeIds.size === filteredRecipes.length ? handleDeselectAll : handleSelectAll}
                        className="text-gray-500 hover:text-menioo-green transition-colors"
                        title={selectedRecipeIds.size === filteredRecipes.length ? 'Désélectionner tout' : 'Sélectionner tout'}
                      >
                        {selectedRecipeIds.size === filteredRecipes.length ? (
                          <CheckSquare className="h-5 w-5" />
                        ) : (
                          <Square className="h-5 w-5" />
                        )}
                      </button>
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recette
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Visibilité
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tags
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Créateur
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRecipes.map((recipe) => (
                    <tr key={recipe.id} className="hover:bg-gray-50 transition-colors">
                      {/* Checkbox */}
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleRecipe(recipe.id)}
                          className="text-gray-400 hover:text-menioo-green transition-colors"
                        >
                          {selectedRecipeIds.has(recipe.id) ? (
                            <CheckSquare className="h-5 w-5 text-menioo-green" />
                          ) : (
                            <Square className="h-5 w-5" />
                          )}
                        </button>
                      </td>
                      
                      {/* Recipe (with image + title) */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          {/* Thumbnail */}
                          <div className="flex-shrink-0 h-12 w-12 rounded overflow-hidden bg-gray-100">
                            {recipe.image ? (
                              <img
                                src={recipe.image}
                                alt={recipe.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center">
                                <ChefHat className="h-6 w-6 text-gray-300" />
                              </div>
                            )}
                          </div>
                          {/* Title */}
                          <div className="max-w-xs">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {recipe.name}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Visibility */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {recipe.isPublic ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            <Globe className="h-3 w-3" />
                            Publique
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full">
                            <Lock className="h-3 w-3" />
                            Privée
                          </span>
                        )}
                      </td>

                      {/* Date */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Calendar className="h-4 w-4" />
                          {formatDate(recipe.createdAt)}
                        </div>
                      </td>

                      {/* Tags (from cookbook) - with inline editor */}
                      <td className="px-6 py-4">
                        <TagEditor
                          recipeId={recipe.id}
                          currentTags={recipeTags[recipe.id] || []}
                          availableTags={availableTags}
                          onAddTag={handleAddTag}
                          onRemoveTag={handleRemoveTag}
                          disabled={isBulkUpdating}
                        />
                      </td>

                      {/* Creator */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {recipe.createdBy
                            ? typeof recipe.createdBy === 'string'
                              ? recipe.createdBy
                              : recipe.createdBy.name
                            : '-'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedRecipeIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <div className="bg-white rounded-lg shadow-2xl border-2 border-menioo-green p-4 min-w-[400px]">
            <div className="flex items-center justify-between gap-6">
              {/* Selection info */}
              <div className="flex items-center gap-3">
                <div className="bg-menioo-green/10 p-2 rounded-lg">
                  <CheckSquare className="h-5 w-5 text-menioo-green" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {selectedRecipeIds.size} recette{selectedRecipeIds.size > 1 ? 's' : ''} sélectionnée{selectedRecipeIds.size > 1 ? 's' : ''}
                  </div>
                  {isBulkUpdating && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      Mise à jour: {bulkUpdateProgress.current}/{bulkUpdateProgress.total}
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleBulkVisibilityChange(true)}
                  disabled={isBulkUpdating}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isBulkUpdating ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <Globe className="h-4 w-4" />
                  )}
                  Rendre public
                </button>
                
                <button
                  onClick={() => handleBulkVisibilityChange(false)}
                  disabled={isBulkUpdating}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isBulkUpdating ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <Lock className="h-4 w-4" />
                  )}
                  Rendre privé
                </button>

                <div className="w-px h-8 bg-gray-300"></div>

                <button
                  onClick={() => setShowBulkTagModal(true)}
                  disabled={isBulkUpdating}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Gérer les tags"
                >
                  <TagsIcon className="h-4 w-4" />
                  Tags
                </button>

                <button
                  onClick={handleDeselectAll}
                  disabled={isBulkUpdating}
                  className="ml-2 p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Annuler la sélection"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Tag Modal */}
      {showBulkTagModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TagsIcon className="h-5 w-5 text-purple-600" />
              Gérer les tags ({selectedRecipeIds.size} recette{selectedRecipeIds.size > 1 ? 's' : ''})
            </h3>

            {/* Action selector */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Action
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setBulkTagAction('add')}
                  className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                    bulkTagAction === 'add'
                      ? 'border-green-600 bg-green-50 text-green-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Ajouter un tag
                </button>
                <button
                  onClick={() => setBulkTagAction('remove')}
                  className={`flex-1 px-4 py-2 rounded-lg border-2 transition-colors ${
                    bulkTagAction === 'remove'
                      ? 'border-red-600 bg-red-50 text-red-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Retirer un tag
                </button>
              </div>
            </div>

            {/* Tag selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionnez un tag
              </label>
              {availableTags.length === 0 ? (
                <p className="text-sm text-gray-500">Aucun tag disponible pour cette organisation</p>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-2">
                  {availableTags.map((tag) => (
                    <button
                      key={tag.id}
                      onClick={() => handleBulkTagChange(tag.id, bulkTagAction)}
                      disabled={isBulkUpdating}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-left"
                    >
                      <div
                        className="w-4 h-4 rounded-full flex-shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span className="text-sm text-gray-900">{tag.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBulkTagModal(false)}
                disabled={isBulkUpdating}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty State (no org selected) */}
      {!selectedOrgId && !isLoadingOrgs && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <ChefHat className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Sélectionnez une organisation
          </h3>
          <p className="text-gray-500">
            Choisissez une organisation dans le menu déroulant ci-dessus pour voir ses recettes
          </p>
        </div>
      )}
    </div>
  );
};

